import {inject, Injectable} from '@angular/core';
import {firstValueFrom, map, Observable, timeout} from 'rxjs';
import {ApiService} from './api.service';
import {ConnectOAuthPopupService} from '../connect/connect-oauth-popup.service';
import {ConnectConnectionsService, ConnectOAuthAuthorizationRequest} from '../connect/connect-connections.service';
import {EnrichConsoleDiagnosticsService} from './enrich-console-diagnostics.service';

export const COMPANY_CAPTURE_CONTACT_SCOPE = 'crm.objects.contacts.read';

/**
 * Service for handling HubSpot authentication
 * This service manages the OAuth flow for HubSpot integration
 */
@Injectable({
  providedIn: 'root'
})
export class HubspotAuthService {
    private apiService = inject(ApiService);
    private oauthPopup = inject(ConnectOAuthPopupService);
    private connections = inject(ConnectConnectionsService);
    private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);

    private readonly providerRef = 'hubspot.oauth';
    private readonly connectionRef = 'hubspot';
    private readonly scopeSet = 'enrich';

  /** Opens a browser-bound flow and completes only after the protected callback. */
  async initiateAuth(clientId: string, connectionRef?: string, options: {companyCapture?: boolean; signal?: AbortSignal} = {}): Promise<boolean> {
    try {
      const request: ConnectOAuthAuthorizationRequest = {
        provider_ref: this.providerRef, client_id: clientId,
        connection_ref: (connectionRef || this.connectionRef).trim(), scope_set: this.scopeSet,
        ...(options.companyCapture ? {extra_scopes: [COMPANY_CAPTURE_CONTACT_SCOPE]} : {}),
      };
      if (connectionRef?.trim()) {
        // Open the popup synchronously, then resolve the selected connection's exact provider identity.
        await this.oauthPopup.authorizeWith(async nonce => {
          const connection = await firstValueFrom(this.connections.get(request.connection_ref!).pipe(timeout(10000)));
          const providerRef = connection.provider_ref || connection.ref;
          if (connection.id !== request.connection_ref || !['hubspot', 'hubspot.oauth', 'hubspot.oauth2'].includes(providerRef?.toLowerCase())) {
            throw new Error('Select a HubSpot connection before reconnecting');
          }
          if (options.signal?.aborted) throw new Error('Authentication was cancelled');
          return firstValueFrom(this.connections.authorize({...request, provider_ref: providerRef, browser_nonce: nonce}));
        }, options.signal);
      } else if (options.signal) await this.oauthPopup.authorize(request, options.signal);
      else await this.oauthPopup.authorize(request);
      return true;
    } catch (error) {
      this.enrichDiagnostics.reportError('enrich.hubspot-auth.initiate', error);
      return false;
    }
  }

  checkCompanyCaptureAccess(connectionId: string): Observable<boolean> {
    return this.connections.getPermissions(connectionId, {requiredScopes: [COMPANY_CAPTURE_CONTACT_SCOPE]}).pipe(
      timeout(10000),
      map(permissions => permissions.connectionId === connectionId &&
        ['hubspot', 'hubspot.oauth', 'hubspot.oauth2'].includes(String(permissions.providerRef || '').toLowerCase()) &&
        permissions.connected === true && permissions.hasRequiredScopes === true &&
        permissions.source === 'metadata.oauthPermissions.grantedScopes' &&
        permissions.grantedScopes?.includes(COMPANY_CAPTURE_CONTACT_SCOPE) === true),
    );
  }

  /**
   * Checks if the client has a valid HubSpot authentication
   * @param clientId The client ID to check
   * @returns An Observable with the authentication status
   */
  checkAuthStatus(clientId: string): Observable<{ authenticated: boolean, error?: string }> {
    return this.apiService.get<{ authenticated: boolean, error?: string }>('hubspot/auth-check', { clientId });
  }

    checkAuthStatusForConnection(
        clientId: string,
        connectionId: string | null | undefined,
    ): Observable<{ authenticated: boolean, error?: string }> {
        const conn = (connectionId || '').trim();
        return this.apiService.get<{ authenticated: boolean, error?: string }>('hubspot/auth-check', {
            clientId,
            ...(conn ? {connectionId: conn} : {}),
        });
    }

  /**
   * Manually triggers a refresh of the HubSpot access token
   * @param clientId The client ID to refresh the token for
   * @returns An Observable with the refresh result
   */
  refreshToken(clientId: string): Observable<{ message: string }> {
      return this.apiService.post<{ message: string }>('hubspot/refresh-token', {}, {clientId});
  }
}
